import { CheckboxInput } from 'component/Checkbox';
import { Map } from 'component/Map';
import mapboxgl from 'mapbox-gl';
import React, { useEffect, useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import moment from 'moment';
import { featureCollection } from '@turf/helpers';
import centroid from '@turf/centroid';
import APIcon from 'assets/images/eo-found-icons/ap1.png';
import AVIcon from 'assets/images/eo-found-icons/av.png';
import BloodyTrapIcon from 'assets/images/eo-found-icons/boody-trap.png';
import AntiShipIcon from 'assets/images/eo-found-icons/anti-ship.png';
import RocketIcon from 'assets/images/eo-found-icons/rocket.png';
import MortarIcon from 'assets/images/eo-found-icons/mortar.png';
import GrenadeIcon from 'assets/images/eo-found-icons/grenade.png';
import FuzeIcon from 'assets/images/eo-found-icons/fuze.png';
import ProjectileIcon from 'assets/images/eo-found-icons/projectile.png';
import EngeneeringChargesIcon from 'assets/images/eo-found-icons/engeneering_charges.png';
import AircraftBombIcon from 'assets/images/eo-found-icons/aircraft_bomb.png';
import ExplosiveIcon from 'assets/images/eo-found-icons/explosive.png';
import SaaIcon from 'assets/images/eo-found-icons/saa.png';
import SubmunitionIcon from 'assets/images/eo-found-icons/submunition.png';
import TorpedoIcon from 'assets/images/eo-found-icons/torpedo.png';
import { SmallLoader } from 'component/Loader';
import { convertCoords3857To4326 } from 'utils/Helper';
import { CategoryTranslations } from 'consts/Daily';
import bbox from '@turf/bbox';
import { Legend } from './Legend';

import { ReactComponent as LeftIcon } from 'assets/images/icons/Left.svg';
import { ReactComponent as RightIcon } from 'assets/images/icons/Right.svg';
import { useHAReports } from 'hooks/useHAReports';
import { HASwitchers } from 'component/HASwitchers';
import { useBufferZone } from 'hooks/useBufferZone';

const getEOFoundGeojson = (categories, zone) => {
  const features = [];
  Object.keys(categories)?.forEach((category) => {
    categories[category]?.evidences?.forEach((evidence) => {
      if (!evidence?.lat || !evidence?.long) return;

      const coords = convertCoords3857To4326([Number(evidence?.long), Number(evidence?.lat)], zone);

      if (!coords) return;

      features.push({
        type: 'Feature',
        properties: {
          long: evidence?.long,
          lat: evidence?.lat,
          category: CategoryTranslations[category][evidence?.item],
        },
        geometry: {
          type: 'Point',
          coordinates: coords,
        },
      });
    });
  });

  return featureCollection(features);
};

const getGridCountsGeojson = (gridGeojson, methodTypes) => {
  const validNames = methodTypes.map((m) => String(m.name));
  const features =
    gridGeojson?.features
      ?.filter((f) => {
        const methods = Array.isArray(f.properties.methods)
          ? f.properties.methods
          : JSON.parse(f.properties.methods || '[]');
        const methodNames = methods?.map((method) => String(method));
        const validCount = methodNames.filter((name) => validNames.includes(name)).length;
        return validCount > 1;
      })
      .map((f) => {
        const center = centroid(f);
        const methods = Array.isArray(f.properties.methods)
          ? f.properties.methods
          : JSON.parse(f.properties.methods || '[]');
        const methodNames = methods.map((method) => String(method));
        const validCount = methodNames.filter((name) => validNames.includes(name)).length;
        center.properties = {
          count: validCount,
          id: f.id,
        };
        return center;
      }) || [];
  return featureCollection(features);
};
function createPerpendicularLineThroughPoint(lineCoords, targetPoint, length) {
  const lineStart = { type: 'Point', coordinates: lineCoords[0] };
  const lineEnd = { type: 'Point', coordinates: lineCoords[1] };

  const dx = lineEnd.coordinates[0] - lineStart.coordinates[0];
  const dy = lineEnd.coordinates[1] - lineStart.coordinates[1];
  const lineBearing = (Math.atan2(dy, dx) * 180) / Math.PI;

  const perpendicularBearing1 = (lineBearing + 90) % 360;
  const perpendicularBearing2 = (lineBearing - 90) % 360;

  const perpendicularPoint1 = [
    targetPoint[0] + length * Math.cos((perpendicularBearing1 * Math.PI) / 180),
    targetPoint[1] + length * Math.sin((perpendicularBearing1 * Math.PI) / 180),
  ];
  const perpendicularPoint2 = [
    targetPoint[0] + length * Math.cos((perpendicularBearing2 * Math.PI) / 180),
    targetPoint[1] + length * Math.sin((perpendicularBearing2 * Math.PI) / 180),
  ];

  return {
    type: 'LineString',
    coordinates: [perpendicularPoint1, perpendicularPoint2],
  };
}

const createSquarePolygon = (center, xAxis) => {
  if (!center || !xAxis) return null;

  const size = 0.00002;
  return {
    type: 'LineString',
    coordinates: [
      center,
      [center[0] + size, center[1]],
      [center[0] + size, center[1] + size],
      [center[0], center[1] + size],
      center,
    ],
  };
};

const createCrosshairGeoJSON = (center, edgeDirection) => {
  const dx = 0.00002 * Math.cos(edgeDirection);
  const dy = 0.00002 * Math.sin(edgeDirection);

  const xAxisCoords = [
    [center[0] - dx, center[1] - dy],
    [center[0] + dx, center[1] + dy],
  ];

  const perpendicularLine = createPerpendicularLineThroughPoint(xAxisCoords, center, 0.00002);
  const yAxisCoords = perpendicularLine.coordinates;
  const squarePolygon = createSquarePolygon(center, xAxisCoords);

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: xAxisCoords,
        },
        properties: {},
      },
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: yAxisCoords,
        },
        properties: {},
      },
      {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: center,
        },
        properties: {},
      },
      squarePolygon
        ? {
            type: 'Feature',
            geometry: squarePolygon,
            properties: {},
          }
        : {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
            properties: {},
          },
    ],
  };
};

const initEOFoundHover = (mapInstance) => {
  let hoveredStateId = null;
  const popupOnHover = new mapboxgl.Popup({
    closeButton: false,
    closeOnClick: false,
  });

  const mousemoveHandler = (e) => {
    if (e.features.length > 0) {
      mapInstance.getCanvas().style.cursor = 'pointer';
      if (hoveredStateId !== null) {
        mapInstance.setFeatureState({ source: 'eo-found', id: hoveredStateId }, { hover: false });
      }
      hoveredStateId = e.features[0].id;
      mapInstance.setFeatureState({ source: 'eo-found', id: hoveredStateId }, { hover: true });
    }
    const html = `
      <div class="flex-column-group">
        <span>${e.features[0]?.properties?.category}</span>
        <span>lat: ${e.features[0]?.properties?.lat}</span>
        <span>long: ${e.features[0]?.properties?.long}</span>
      </div>
    `;
    popupOnHover.setLngLat(e.lngLat).setHTML(html).addTo(mapInstance);
    popupOnHover.addClassName('hover-progress-map-popup');
  };

  const mouseleaveHandler = () => {
    mapInstance.getCanvas().style.cursor = '';
    if (hoveredStateId !== null) {
      mapInstance.setFeatureState({ source: 'eo-found', id: hoveredStateId }, { hover: false });
    }
    hoveredStateId = null;
    popupOnHover.remove();
  };

  mapInstance.on('mousemove', 'eo-found', mousemoveHandler);
  mapInstance.on('mouseleave', 'eo-found', mouseleaveHandler);

  return () => {
    mapInstance.off('mousemove', 'eo-found', mousemoveHandler);
    mapInstance.off('mouseleave', 'eo-found', mouseleaveHandler);
    popupOnHover.remove();
  };
};
const setEOFoundGeojsonToMap = (map, geojson) => {
  if (!map) return null;
  if (!map.getSource('eo-found')) {
    [
      { 'ap-icon': APIcon },
      { 'av-icon': AVIcon },
      { 'bloody-trap-icon': BloodyTrapIcon },
      { 'anti-ship-icon': AntiShipIcon },
      { 'rocket-icon': RocketIcon },
      { 'mortar-icon': MortarIcon },
      { 'grenade-icon': GrenadeIcon },
      { 'fuze-icon': FuzeIcon },
      { 'projectile-icon': ProjectileIcon },
      { 'engeneering-charges-icon': EngeneeringChargesIcon },
      { 'aircraft-bomb-icon': AircraftBombIcon },
      { 'explosive-icon': ExplosiveIcon },
      { 'saa-icon': SaaIcon },
      { 'submunition-icon': SubmunitionIcon },
      { 'torpedo-icon': TorpedoIcon },
    ].forEach((icon) => {
      map.loadImage(Object.values(icon)[0], (error, image) => {
        if (error) throw error;
        map.addImage(Object.keys(icon)[0], image);
      });
    });
    map.addSource('eo-found', {
      type: 'geojson',
      data: geojson,
      generateId: true,
    });
    map.addLayer({
      id: 'eo-found',
      type: 'symbol',
      source: 'eo-found',
      layout: {
        'icon-image': [
          'match',
          ['get', 'category'],
          'AP',
          'ap-icon',
          'AP (controlled)',
          'ap-icon',
          'AV',
          'av-icon',
          'Bloody trap',
          'bloody-trap-icon',
          'Anti-ship',
          'anti-ship-icon',
          'Rocket',
          'rocket-icon',
          'Mortar',
          'mortar-icon',
          'Grenade',
          'grenade-icon',
          'Fuze',
          'fuze-icon',
          'Projectile',
          'projectile-icon',
          'Engeneering charges',
          'engeneering-charges-icon',
          'Aircraft bomb',
          'aircraft-bomb-icon',
          'Explosive',
          'explosive-icon',
          'SAA',
          'saa-icon',
          'Submunition',
          'submunition-icon',
          'Torpedo',
          'torpedo-icon',
          'ap-icon',
        ],
        'icon-size': 0.1,
        'icon-offset': [0, 0],
        'icon-allow-overlap': true,
      },
    });
    return initEOFoundHover(map);
  } else {
    map.getSource('eo-found').setData(geojson);
    return null;
  }
};
export const DailyMap = ({
  map,
  setMap,
  methodTypes,
  formData,
  gridGeojson,
  setGridGeojson,
  loader,
  zone,
  haReportPolygon,
  mode,
  crosshair,
}) => {
  useBufferZone(map);
  const haReportsDailyMap = useHAReports(map);
  // eslint-disable-next-line no-unused-vars
  const [polygonGeojson, setPolygonGeojson] = useState(haReportPolygon);
  const [isLegendOpen, setIsLegendOpen] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  const selectedPolygonIdRef = useRef(null);
  const eoFoundCleanupRef = useRef(null);
  const gridClickCleanupRef = useRef(null);
  const notUpdatedGrids = new Set(
    gridGeojson?.features
      ?.filter((f) => {
        const colors = Array.isArray(f.properties.colors)
          ? f.properties.colors
          : typeof f.properties.colors === 'string'
            ? JSON.parse(f.properties.colors || '[]')
            : [];
        return colors.length > 0;
      })
      .map((f) => f.properties.id)
  );
  const openLegend = () => setIsLegendOpen(true);
  const closeLegend = () => setIsLegendOpen(false);

  const fitMapToBounds = (map, geojson) => {
    if (map && geojson?.features?.length > 0) {
      const bounds = bbox(geojson);
      map.fitBounds(bounds, {
        padding: 50,
      });
    }
  };

  const initGridPolygonHover = (mapInstance) => {
    return;
  };
  const changePolygonColor = (colors, feature, selectedMethods) => {
    setGridGeojson((prevGridGeojson) => {
      const updatedGridFeatures = prevGridGeojson.features.map((f) => {
        if (f.id === feature.properties.id) {
          return {
            ...f,
            properties: {
              ...f.properties,
              colors: colors || [],
              additional_colors: colors || [],
              updated_date: new Date().toISOString(),
              methods: selectedMethods || [],
              methods_ids: (selectedMethods || [])
                .map((methodName) => {
                  const method = methodTypes.find((m) => String(m.name) === String(methodName));
                  return method ? Number(method.id) : null;
                })
                .filter((id) => id !== null),
              render_colors: colors || [],
            },
          };
        }
        return f;
      });
      return {
        ...prevGridGeojson,
        features: updatedGridFeatures,
      };
    });
  };
  const initGridPolygonClick = (mapInstance) => {
    let fixedPopupElement = null;
    let popupRoot = null;

    const createFixedPopup = () => {
      if (fixedPopupElement) {
        fixedPopupElement?.remove();
        if (popupRoot) {
          popupRoot?.unmount();
        }
      }

      fixedPopupElement = document.createElement('div');
      fixedPopupElement.className = 'fixed-map-popup';

      const switchersElement = document.querySelector('.mapbox-control');
      if (switchersElement) {
        const switchersRect = switchersElement.getBoundingClientRect();
        const mapContainer = mapInstance.getContainer();
        const mapRect = mapContainer.getBoundingClientRect();

        fixedPopupElement.style.left = `${switchersRect.left - mapRect.left}px`;
        fixedPopupElement.style.top = `${switchersRect.bottom - mapRect.top + 15}px`;
      } else {
        fixedPopupElement.style.left = '10px';
        fixedPopupElement.style.top = '200px';
      }

      mapInstance.getContainer().appendChild(fixedPopupElement);
      return fixedPopupElement;
    };

    const removeFixedPopup = () => {
      if (fixedPopupElement) {
        fixedPopupElement.remove();
        fixedPopupElement = null;
      }
      if (popupRoot) {
        popupRoot.unmount();
        popupRoot = null;
      }
    };

    const generalClickHandler = () => {
      removeFixedPopup();
      if (selectedPolygonIdRef?.current !== null) {
        mapInstance.setFeatureState(
          { source: 'grid', id: selectedPolygonIdRef.current },
          { selected: false }
        );
        selectedPolygonIdRef.current = null;
        setSelectedPolygonId(null);
      }
    };

    mapInstance.on('click', generalClickHandler);

    const gridPolygonClickHandler = (e) => {
      // Only block clicks for edit/create modes when grids have colors
      if ((mode === 'edit' || mode === 'create') && notUpdatedGrids.has(e.features[0].id)) return;

      const currentFeature = e.features[0];
      const clickedFeatureId = currentFeature.id || currentFeature.properties.id;

      if (mode === 'view' || mode === 'validation') {
        let methods = currentFeature.properties.methods;

        if (Array.isArray(methods)) {
          if (!methods || methods.length === 0) {
            return;
          }
        } else if (typeof methods === 'string') {
          try {
            methods = JSON.parse(methods || '[]');
          } catch {
            methods = [];
          }
          if (!methods || methods.length === 0) {
            return;
          }
        } else {
          return;
        }
      }

      if (selectedPolygonIdRef?.current !== null) {
        mapInstance.setFeatureState(
          { source: 'grid', id: selectedPolygonIdRef.current },
          { selected: false }
        );
      }

      mapInstance.setFeatureState({ source: 'grid', id: clickedFeatureId }, { selected: true });
      selectedPolygonIdRef.current = clickedFeatureId;
      setSelectedPolygonId(clickedFeatureId);

      const popupElement = createFixedPopup();
      popupRoot = createRoot(popupElement);

      const popupKey = `${clickedFeatureId}-${JSON.stringify(currentFeature.properties.colors)}-${JSON.stringify(currentFeature.properties.methods)}`;

      if (!currentFeature) {
        console.error('Feature not found in click event.');
        return;
      }

      const PopupComponent =
        mode === 'view' || mode === 'validation' ? PopupContentView : PopupContent;

      popupRoot.render(
        <PopupComponent
          key={popupKey}
          changePolygonColor={mode === 'edit' || mode === 'create' ? changePolygonColor : undefined}
          feature={currentFeature}
          methodTypes={methodTypes}
          mode={mode}
          onClose={() => {
            removeFixedPopup();
            if (selectedPolygonIdRef.current !== null) {
              mapInstance.setFeatureState(
                { source: 'grid', id: selectedPolygonIdRef.current },
                { selected: false }
              );
              selectedPolygonIdRef.current = null;
              setSelectedPolygonId(null);
            }
          }}
        />
      );
    };

    mapInstance.on('click', 'grid-polygon', gridPolygonClickHandler);

    return () => {
      mapInstance.off('click', generalClickHandler);
      mapInstance.off('click', 'grid-polygon', gridPolygonClickHandler);
      removeFixedPopup();
    };
  };
  const setPolygonGeojsonToMap = (geojson) => {
    setPolygonGeojson(geojson);
    if (!map) return;
    if (!map.getSource('polygon')) {
      map.addSource('polygon', {
        type: 'geojson',
        data: polygonGeojson,
      });
      map.addLayer({
        id: 'polygon',
        type: 'fill',
        source: 'polygon',
        layout: {},
        paint: {
          'fill-color': '#FF3F3F',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            0,
            1,
            14,
            0.6,
            16,
            0.4,
            18,
            0.3,
            20,
            0.2,
            22,
            0.1,
          ],
        },
      });
      map.addLayer({
        id: 'polygon-outline',
        type: 'line',
        source: 'polygon',
        layout: {},
        paint: {
          'line-color': '#FF0000',
          'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.5, 14, 0.5, 18, 0.8, 22, 1.0],
          'line-opacity': 0.7,
        },
      });
    } else {
      map.getSource('polygon').setData(geojson);
    }
  };
  useEffect(() => {
    if (!map) return;
    if (!polygonGeojson) return;
    setPolygonGeojsonToMap(polygonGeojson);
  }, [map, polygonGeojson]);

  useEffect(() => {
    if (map && gridGeojson?.features?.length > 0 && isInitialLoad) {
      const bounds = bbox(gridGeojson);
      map.fitBounds(bounds, {
        padding: 50,
      });
      setIsInitialLoad(false);
    }
  }, [map, gridGeojson, isInitialLoad]);
  useEffect(() => {
    if (!haReportPolygon) return;
    setPolygonGeojson(haReportPolygon);
  }, [haReportPolygon]);

  useEffect(() => {
    selectedPolygonIdRef.current = selectedPolygonId;
  }, [selectedPolygonId]);
  // useEffect(() => {
  //   if (map && gridGeojson?.features?.length > 0) {
  //     const bounds = bbox(gridGeojson);
  //     map.fitBounds(bounds, {
  //       padding: 50,
  //     });
  //   }
  // }, [map, gridGeojson, gridGeojson?.features?.length]);

  useEffect(() => {
    if (!map) return;
    if (!methodTypes?.length) return;
    if (!map.getSource('grid')) {
      gridGeojson?.features?.forEach((gridFeature) => {
        const parsedColors =
          typeof gridFeature?.properties?.colors === 'string'
            ? JSON.parse(gridFeature.properties.colors || '[]')
            : gridFeature.properties.colors || [];
        const parsedMethods =
          typeof gridFeature?.properties?.methods === 'string'
            ? JSON.parse(gridFeature?.properties?.methods || '[]')
            : gridFeature?.properties?.methods || [];
        const parsedRenderColors =
          typeof gridFeature?.properties?.render_colors === 'string'
            ? JSON.parse(gridFeature?.properties?.render_colors || '[]')
            : gridFeature?.properties?.render_colors || [];
        gridFeature.properties.additional_colors = parsedColors;
        gridFeature.properties.methods = parsedMethods;
        gridFeature.properties.render_colors = parsedRenderColors;
      });
      map.addSource('grid', {
        type: 'geojson',
        data: gridGeojson,
      });
      map.addLayer({
        id: 'grid-polygon',
        type: 'fill',
        source: 'grid',
        layout: {},
        paint: {
          'fill-color': [
            'case',
            ['==', ['length', ['get', 'render_colors']], 0],
            'transparent',
            [
              'to-color',
              ['at', ['-', ['length', ['get', 'render_colors']], 1], ['get', 'render_colors']],
            ],
          ],
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            0.9,
            ['boolean', ['feature-state', 'hover'], false],
            0.8,
            0.6,
          ],
        },
      });
      map.addLayer({
        id: 'grid-line',
        type: 'line',
        source: 'grid',
        layout: {},
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'selected'], false],
            '#00b1b7',
            '#f00',
          ],
          'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 3, 1],
          'line-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 1, 0.8],
        },
      });
      const gridCountsSourceId = 'grid-counts';
      const gridCountsGeojson = getGridCountsGeojson(gridGeojson, methodTypes);

      if (!map.getSource(gridCountsSourceId)) {
        map.addSource(gridCountsSourceId, {
          type: 'geojson',
          data: gridCountsGeojson,
        });

        map.addLayer({
          id: 'grid-count-circle',
          type: 'circle',
          source: gridCountsSourceId,
          minzoom: 19,
          paint: {
            'circle-radius': 12,
            'circle-color': '#ffffff',
            'circle-stroke-width': 1.5,
            'circle-stroke-color': '#000000',
            'circle-opacity': 0.9,
          },
        });

        map.addLayer({
          id: 'grid-count-text',
          type: 'symbol',
          source: gridCountsSourceId,
          minzoom: 19,
          layout: {
            'text-field': ['get', 'count'],
            'text-size': 14,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
            'text-allow-overlap': true,
            'text-ignore-placement': true,
          },
          paint: {
            'text-color': '#000000',
          },
        });
      }
      gridClickCleanupRef.current = initGridPolygonClick(map);
      initGridPolygonHover(map);
      if (gridGeojson && gridGeojson.features && gridGeojson.features.length > 0) {
        const bounds = bbox(gridGeojson);
        map.fitBounds(bounds);
      }
      fitMapToBounds(map, gridGeojson);
    }

    return () => {
      gridClickCleanupRef.current?.();
      gridClickCleanupRef.current = null;
    };
  }, [map, methodTypes]);
  useEffect(() => {
    if (!map) return;
    if (!methodTypes?.length) return;
    let needsUpdate = false;
    gridGeojson?.features.forEach((feature) => {
      feature.id = feature.properties.id;
      const parsedColors =
        typeof feature.properties.colors === 'string'
          ? JSON.parse(feature.properties.colors || '[]')
          : feature.properties.colors || [];
      const parsedMethods =
        typeof feature.properties.methods === 'string'
          ? JSON.parse(feature.properties.methods || '[]')
          : feature.properties.methods || [];
      feature.properties.additional_colors = parsedColors;
      feature.properties.methods = parsedMethods;

      if (feature.properties.render_colors === undefined) {
        feature.properties.render_colors = parsedColors;
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      map.getSource('grid').setData(gridGeojson);
    }

    if (map.getSource('grid-counts')) {
      map.getSource('grid-counts').setData(getGridCountsGeojson(gridGeojson, methodTypes));
    }
  }, [map, gridGeojson, methodTypes, mode]);

  useEffect(() => {
    if (!map) return;
    if (!methodTypes?.length) return;
    if (mode !== 'view' && mode !== 'validation') return;

    let needsUpdate = false;
    gridGeojson?.features.forEach((feature) => {
      const parsedColors =
        typeof feature.properties.colors === 'string'
          ? JSON.parse(feature.properties.colors || '[]')
          : feature.properties.colors || [];

      if (!feature.properties.render_colors || feature.properties.render_colors.length === 0) {
        feature.properties.render_colors = parsedColors;
        needsUpdate = true;
      }
    });

    if (needsUpdate && map.getSource('grid')) {
      map.getSource('grid').setData(gridGeojson);
    }
  }, [map, gridGeojson?.features?.length, methodTypes?.length, mode]);

  useEffect(() => {
    if (!map) return;

    const geojson = getEOFoundGeojson(formData?.info?.eo_found_categories || [], zone);
    const cleanup = setEOFoundGeojsonToMap(map, geojson);
    if (cleanup) eoFoundCleanupRef.current = cleanup;

    return () => {
      eoFoundCleanupRef.current?.();
      eoFoundCleanupRef.current = null;
    };
  }, [formData?.info?.eo_found_categories, zone, map]);

  useEffect(() => {
    if (!map) return;
    if (!methodTypes?.length) return;
    gridGeojson?.features.forEach((feature) => {
      feature.id = feature.properties.id;
      const parsedColors =
        typeof feature.properties.colors === 'string'
          ? JSON.parse(feature.properties.colors || '[]')
          : feature.properties.colors || [];
      feature.properties.additional_colors = parsedColors;

      if (feature.properties.render_colors === undefined) {
        feature.properties.render_colors = parsedColors;
      }
    });
    map.getSource('grid').setData(gridGeojson);
  }, [map, gridGeojson, methodTypes, mode]);

  useEffect(() => {
    if (!map || !crosshair || !gridGeojson?.features?.length || loader) return;

    if (!map.getSource('crosshair')) {
      map.addSource('crosshair', {
        type: 'geojson',
        data: crosshair,
      });

      map.addLayer({
        id: 'crosshair-layer',
        type: 'line',
        source: 'crosshair',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#000000',
          'line-width': 1,
        },
      });
    } else {
      map.getSource('crosshair').setData(crosshair);
    }
  }, [map, crosshair, gridGeojson, loader]);

  return (
    <div className="daily-map">
      <div style={{ position: 'relative' }}>
        <HASwitchers
          {...haReportsDailyMap}
          googleWms={true}
          style={{
            position: 'absolute',
            top: '66px',
            zIndex: 1,
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '4px',
            padding: '0px 15px 7px 10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            left: '0px',
            borderRadius: '0 0 8px',
            boxShadow: '0px 20px 25px 0px rgba(76, 103, 100, 0.1)',
          }}
        />
        <Map
          map={map}
          setMap={setMap}
          turnPoints={haReportPolygon}
          haReportsPolygons={haReportsDailyMap?.haReportsPolygons}
          // azureLayerEnabled={haReportsDailyMap?.azureLayerEnabled}
          // setAzureLayerEnabled={haReportsDailyMap?.setAzureLayerEnabled}
          googleWms
        />
      </div>
      {gridGeojson && (
        <>
          <div className={`legend-container ${isLegendOpen ? 'open' : 'closed'}`}>
            <Legend
              map={map}
              colors={methodTypes?.map((method) => method?.color)}
              gridGeojson={gridGeojson}
              setGridGeojson={setGridGeojson}
              methodTypes={methodTypes}
            />
            <RightIcon
              className="close-legend"
              onClick={closeLegend}
              style={{ cursor: 'pointer' }}
            />
          </div>
          {!isLegendOpen && (
            <LeftIcon className="open-legend" onClick={openLegend} style={{ cursor: 'pointer' }} />
          )}
        </>
      )}
      <div className="daily-map-loader">
        <SmallLoader visible={loader} />
      </div>
    </div>
  );
};

const PopupContentView = ({ feature, methodTypes, onClose }) => {
  const normalizedMethodTypes = methodTypes.map((m) => ({ ...m, name: String(m.name) }));

  function getCurrentMethods() {
    let methods = feature?.properties?.methods;

    if (Array.isArray(methods)) {
      return methods.map((method) => String(method));
    }

    if (typeof methods === 'string') {
      try {
        methods = JSON.parse(methods);
      } catch {
        methods = [];
      }
    }

    return (methods || []).map((method) => String(method));
  }

  const currentMethods = getCurrentMethods();
  const activeMethods = normalizedMethodTypes.filter((method) =>
    currentMethods.includes(String(method.name))
  );

  const updatedDate = feature?.properties?.updated_date
    ? moment(feature?.properties?.updated_date).format('DD.MM.YYYY')
    : null;

  return (
    <div>
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '0px',
          right: '10px',
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          color: '#18afba',
          padding: '2px',
          margin: '0',
          lineHeight: '1',
          zIndex: '10',
        }}
      >
        x
      </button>
      <div className="flex-column-group" style={{ paddingLeft: '10px' }}>
        {activeMethods?.map((method) => (
          <div
            key={method?.id}
            className="flex-group"
            style={{ marginBottom: '8px', alignItems: 'flex-start' }}
          >
            <div
              className="color-item"
              style={{
                backgroundColor: method?.color,
                width: '18px',
                height: '18px',
                borderRadius: '0',
                display: 'flex',
                flexShrink: 0,
                marginRight: '8px',
                marginTop: '2px',
              }}
            />
            <span
              style={{
                fontFamily: 'Montserrat-Regular',
                color: '#abaaae',
                fontSize: '15px',
                fontWeight: 500,
                lineHeight: '1.3',
              }}
            >
              {method?.name}
            </span>
          </div>
        ))}
        {updatedDate && (
          <div
            style={{
              color: '#abaaae',
              fontSize: '15px',
              fontWeight: 500,
              marginTop: '10px',
              textAlign: 'start',
            }}
          >
            {updatedDate}
          </div>
        )}
      </div>
    </div>
  );
};

const PopupContent = ({ changePolygonColor, feature, methodTypes, onClose }) => {
  const normalizedMethodTypes = methodTypes.map((m) => ({ ...m, name: String(m.name) }));

  function initializeMethods() {
    let methods = feature?.properties?.methods;
    if (typeof methods === 'string') {
      try {
        methods = JSON.parse(methods);
      } catch {
        methods = [];
      }
    }
    return (methods || []).map((method) => String(method));
  }

  const [selectedMethods, setSelectedMethods] = useState(() => {
    const initial = initializeMethods();
    return initial;
  });

  useEffect(() => {
    const updated = initializeMethods();
    setSelectedMethods(updated);
  }, [feature?.properties?.id, feature?.properties?.colors, feature?.properties?.methods]);

  if (!feature) return null;

  const handleCheckboxChange = (methodId, checked) => {
    const method = normalizedMethodTypes.find((m) => Number(m.id) === Number(methodId));
    if (!method) return;

    const methodName = String(method.name);
    const validMethodNames = normalizedMethodTypes.map((m) => String(m.name));
    let newSelectedMethods;
    if (checked) {
      newSelectedMethods = [...selectedMethods, methodName];
    } else {
      newSelectedMethods = selectedMethods.filter((name) => name !== methodName);
    }
    newSelectedMethods = newSelectedMethods.filter((name) => validMethodNames.includes(name));
    setSelectedMethods(newSelectedMethods);

    const newColors = [
      ...new Set(
        normalizedMethodTypes
          .filter((method) => newSelectedMethods.includes(String(method.name)))
          .map((method) => method.color)
      ),
    ];

    if (changePolygonColor) {
      changePolygonColor(newColors, feature, newSelectedMethods);
    }
  };

  return (
    <div>
      <button
        onClick={onClose}
        style={{
          position: 'absolute',
          top: '0px',
          right: '10px',
          background: 'none',
          border: 'none',
          fontSize: '20px',
          cursor: 'pointer',
          color: '#18afba',
          padding: '2px',
          margin: '0',
          lineHeight: '1',
          zIndex: '10',
        }}
      >
        x
      </button>
      <div className="flex-column-group">
        {normalizedMethodTypes?.map((method) => {
          return (
            <CheckboxInput
              key={method?.id}
              label={
                <div className="flex-group" style={{ alignItems: 'flex-start' }}>
                  <div
                    className="color-item"
                    style={{
                      backgroundColor: method?.color,
                      width: '18px',
                      height: '18px',
                      borderRadius: '0',
                      display: 'flex',
                      flexShrink: 0,
                      marginRight: '8px',
                      marginTop: '2px',
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'Montserrat-Regular',
                      color: '#abaaae',
                      fontSize: '15px',
                      fontWeight: 500,
                      lineHeight: '1.3',
                    }}
                  >
                    {method?.name}
                  </span>
                </div>
              }
              value={method?.id}
              name={method?.name}
              id={method?.id}
              checked={selectedMethods.includes(String(method?.name))}
              handleInput={(value, checked) => handleCheckboxChange(method?.id, checked)}
            />
          );
        })}
      </div>
    </div>
  );
};
